import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

interface Props {
  /** Current tags. */
  value: string[];
  /** Called with the next list whenever the user adds or removes one. */
  onChange: (next: string[]) => void;
  /** Closed enum the picker draws from. The component itself imposes no
   *  list — each caller passes its own (`VALID_TAGS` for tenant,
   *  `MASTER_TAGS` for admin). Tags already in `value` are excluded from
   *  the popover. */
  availableTags: readonly string[];
  /** Optional label rendered above the chip row (matches Input's label style). */
  label?: string;
  /** Disables both the trigger and the chip remove buttons. Useful for
   *  read-only views (admin viewing a global filament). */
  disabled?: boolean;
  /** Custom label for the "+" trigger. Defaults to "Añadir tag". Tenant
   *  passes `t('catalog.addTag')`; admin can pass a literal. */
  addTagLabel?: string;
  /** Builds the aria-label for a chip's remove button. Receives the tag.
   *  Default English (`Remove {tag}`) to preserve existing behaviour. */
  removeTagLabel?: (tag: string) => string;
}

/**
 * Read-only-from-the-enum tag selector. Displays the current tag list as
 * removable chips and a "+" chip that opens a popover with every tag from
 * ``availableTags`` not yet selected. Users cannot type free-form tags —
 * the OpenPrintTag enum is closed, and any new tag would need a backend
 * change first.
 *
 * Lives in `shared/ui/` because the picker UX is identical between tenant
 * catalogo and platform-admin catalogo. The closed list of tags differs
 * between the two apps so it's passed as a prop rather than imported from
 * a domain module.
 */
export default function TagPicker({
  value,
  onChange,
  availableTags,
  label,
  disabled = false,
  addTagLabel = 'Añadir tag',
  removeTagLabel = (tag: string) => `Remove ${tag}`,
}: Props) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const available = availableTags.filter(t => !value.includes(t));

  const add = (tag: string) => {
    if (value.includes(tag)) return;
    onChange([...value, tag]);
  };

  const remove = (tag: string) => {
    onChange(value.filter(t => t !== tag));
  };

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          {label}
        </label>
      )}
      <div className="flex flex-wrap items-center gap-1.5 min-h-[2.75rem] px-2 py-2 rounded-md border border-border-strong dark:border-border-strong-dark bg-white dark:bg-surface-dark">
        {value.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(tag)}
                className="text-slate-400 hover:text-red-500 transition-colors"
                aria-label={removeTagLabel(tag)}
              >
                <Icon name="close" className="!text-[14px]" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <div className="relative">
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpen(o => !o)}
              disabled={available.length === 0}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-dashed border-border-strong dark:border-border-strong-dark text-slate-500 dark:text-slate-400 hover:border-brand-orange hover:text-brand-orange disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="add" className="!text-[14px]" />
              {addTagLabel}
            </button>
            {open && (
              <div
                ref={popoverRef}
                className="absolute z-50 left-0 top-full mt-1 w-72 max-h-72 overflow-y-auto rounded-lg border border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark shadow-lg p-2"
              >
                <div className="grid grid-cols-1 gap-0.5">
                  {available.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => { add(tag); setOpen(false); }}
                      className="text-left px-2 py-1 rounded text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
